import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchVerificationQueue, 
  fetchProgramVerifications, 
  updateVerification,
  createSampleVerifications,
  type VerificationQueueRow
} from '../../services/rpc/verification';
import { VerificationQueue } from './VerificationQueue';
import { ProgramVerificationDetail } from './ProgramVerificationDetail';

export const EnrichmentVerificationView: React.FC = () => {
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);
  const [selectedProgram, setSelectedProgram] = useState<VerificationQueueRow | null>(null);
  const queryClient = useQueryClient();

  // Fetch verification queue
  const queueQuery = useQuery({
    queryKey: ['verification-queue'],
    queryFn: () => fetchVerificationQueue(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch program details when selected
  const programQuery = useQuery({
    queryKey: ['program-verifications', selectedProgramId],
    queryFn: () => selectedProgramId ? fetchProgramVerifications(selectedProgramId) : Promise.resolve([]),
    enabled: !!selectedProgramId,
  });

  // Mutation for updating verifications
  const updateMutation = useMutation({
    mutationFn: ({
      verificationId,
      userAction,
      correctedValue,
      correctionNotes,
    }: {
      verificationId: string;
      userAction: 'approve' | 'edit' | 'reject' | 'flag_learning';
      correctedValue?: any;
      correctionNotes?: string;
    }) => updateVerification(verificationId, userAction, correctedValue, correctionNotes),
    onSuccess: () => {
      // Refresh both queries
      queryClient.invalidateQueries({ queryKey: ['verification-queue'] });
      queryClient.invalidateQueries({ queryKey: ['program-verifications', selectedProgramId] });
    },
  });

  // Mutation for creating sample data
  const sampleDataMutation = useMutation({
    mutationFn: createSampleVerifications,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['verification-queue'] });
    },
  });

  const handleSelectProgram = (program: VerificationQueueRow) => {
    setSelectedProgramId(program.programId);
    setSelectedProgram(program);
  };

  const handleBackToQueue = () => {
    setSelectedProgramId(null);
    setSelectedProgram(null);
  };

  const handleUpdateVerification = (
    verificationId: string,
    userAction: 'approve' | 'edit' | 'reject' | 'flag_learning',
    correctedValue?: any,
    correctionNotes?: string
  ) => {
    updateMutation.mutate({ verificationId, userAction, correctedValue, correctionNotes });
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>
          Enrichment Verification
        </h1>
        <p style={{ color: '#6b7280', fontSize: '16px', marginBottom: '16px' }}>
          Review and verify AI-extracted program data to ensure accuracy and build learning patterns.
        </p>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={() => queueQuery.refetch()}
            disabled={queueQuery.isFetching}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#ffffff',
              color: '#374151',
              fontWeight: '500',
              cursor: queueQuery.isFetching ? 'not-allowed' : 'pointer',
              opacity: queueQuery.isFetching ? 0.6 : 1,
            }}
          >
            {queueQuery.isFetching ? 'Refreshing...' : 'Refresh Queue'}
          </button>
          
          <button
            onClick={() => sampleDataMutation.mutate()}
            disabled={sampleDataMutation.isPending}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid #3b82f6',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontWeight: '500',
              cursor: sampleDataMutation.isPending ? 'not-allowed' : 'pointer',
              opacity: sampleDataMutation.isPending ? 0.6 : 1,
            }}
          >
            {sampleDataMutation.isPending ? 'Creating...' : 'Create Sample Data'}
          </button>
          
          {sampleDataMutation.isSuccess && (
            <span style={{ color: '#10b981', fontSize: '14px', fontWeight: '500' }}>
              ✓ Sample data created
            </span>
          )}
        </div>
      </div>

      {selectedProgramId && selectedProgram ? (
        <ProgramVerificationDetail
          program={selectedProgram}
          verifications={programQuery.data || []}
          isLoading={programQuery.isLoading}
          onBack={handleBackToQueue}
          onUpdateVerification={handleUpdateVerification}
          isUpdating={updateMutation.isPending}
        />
      ) : (
        <VerificationQueue
          programs={queueQuery.data || []}
          isLoading={queueQuery.isLoading}
          error={queueQuery.error}
          onSelectProgram={handleSelectProgram}
        />
      )}
    </div>
  );
};